/**
 * Fees, Students, Exams, Notifications, Attendance services for the web admin portal.
 * Mirrors /app/frontend/src/services/* and re-uses the same /api contract.
 */
import { http } from "./api";

// ---------------- FEES ----------------
export type InvoiceStatus = "PENDING" | "PARTIAL" | "PAID" | "OVERDUE" | "CANCELLED" | "pending" | "paid";
export type PaymentMethod = "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "CHEQUE" | "OTHER";
export type DiscountType = "PERCENT" | "FLAT";

export interface Invoice {
  id: string; student_id: string; student_name: string; class_name: string;
  amount: number; due_date: string; term: string; status: InvoiceStatus;
  paid_date?: string | null;
  subtotal?: number; discount_total?: number; late_fee_total?: number;
  paid_amount?: number; balance_amount?: number;
  created_at: string; updated_at?: string;
}
export interface Payment {
  id: string; invoice_id: string; student_name: string; class_name: string;
  amount: number; method: PaymentMethod; reference?: string | null; note?: string | null;
  received_by_name: string; received_at: string; receipt_no: string;
  reversed_at?: string | null;
}
export interface Discount {
  id: string; invoice_id: string; type: DiscountType; value: number;
  reason?: string | null; applied_by_name: string; applied_at: string;
}
export interface InvoiceDetail { invoice: Invoice; payments: Payment[]; discounts: Discount[]; }
export interface CollectionReport {
  collected_total: number; payments_count: number;
  by_method: Record<string, number>; by_class: Record<string, number>;
  outstanding_total: number; overdue_total: number; outstanding_invoices: number;
}

export const feesService = {
  list: (params: { status?: InvoiceStatus; class_name?: string; student_id?: string } = {}) =>
    http<Invoice[]>("/fees/invoices", { query: params }),
  detail: (id: string) => http<InvoiceDetail>(`/fees/invoices/${id}`),
  recordPayment: (id: string, data: { amount: number; method: PaymentMethod; reference?: string; note?: string }) =>
    http<{ payment: Payment; invoice: Invoice }>(`/fees/invoices/${id}/payments`, { method: "POST", body: data }),
  reversePayment: (id: string, pid: string) =>
    http<{ ok: true; invoice: Invoice }>(`/fees/invoices/${id}/payments/${pid}`, { method: "DELETE" }),
  applyDiscount: (id: string, data: { type: DiscountType; value: number; reason?: string }) =>
    http<{ discount: Discount; invoice: Invoice }>(`/fees/invoices/${id}/discounts`, { method: "POST", body: data }),
  removeDiscount: (id: string, did: string) =>
    http<{ ok: true; invoice: Invoice }>(`/fees/invoices/${id}/discounts/${did}`, { method: "DELETE" }),
  cancel: (id: string) => http<{ ok: true; invoice: Invoice }>(`/fees/invoices/${id}/cancel`, { method: "POST" }),
  receipt: (pid: string) => http<any>(`/fees/payments/${pid}/receipt`),
  collectionReport: (params: { date_from?: string; date_to?: string } = {}) =>
    http<CollectionReport>("/fees/reports/collection", { query: params }),
  createLegacy: (data: { student_id: string; amount: number; due_date: string; term: string }) =>
    http<Invoice>("/fees", { method: "POST", body: data }),
  bulkGenerate: (data: { class_name: string; amount: number; due_date: string; term: string }) =>
    http<{ created: number; class_name: string }>("/fees/bulk-generate", { method: "POST", body: data }),
};

// ---------------- STUDENTS ----------------
export interface Student {
  id: string; name: string; roll_no: string; class_name: string;
  section?: string | null; gr_no?: string | null;
  date_of_birth: string; gender: string; email: string;
  phone?: string | null; phone_2?: string | null; address?: string | null;
  father_name?: string | null; mother_name?: string | null;
  parent_name?: string | null; parent_phone?: string | null;
  address_line_1?: string | null; address_line_2?: string | null; address_line_3?: string | null;
  state?: string | null; pin_code?: string | null;
  social_category?: string | null; aadhaar_no?: string | null; name_as_per_aadhaar?: string | null;
  photo_base64?: string | null;
  created_at: string;
}

export interface BulkImportRow {
  name: string; class_name: string; section?: string | null; gr_no?: string | null;
  roll_no?: string | null; date_of_birth?: string | null; gender?: string | null;
  phone?: string | null; phone_2?: string | null;
  father_name?: string | null; mother_name?: string | null;
  address_line_1?: string | null; address_line_2?: string | null; address_line_3?: string | null;
  state?: string | null; pin_code?: string | null;
  social_category?: string | null; aadhaar_no?: string | null; name_as_per_aadhaar?: string | null;
}

export interface BulkImportResult {
  created: number; updated: number; skipped: number; total: number;
}

export const studentsService = {
  list: () => http<Student[]>("/students"),
  create: (data: Partial<Student>) => http<Student>("/students", { method: "POST", body: data }),
  update: (id: string, data: Partial<Student>) => http<Student>(`/students/${id}`, { method: "PUT", body: data }),
  remove: (id: string) => http<{ ok: true }>(`/students/${id}`, { method: "DELETE" }),
  bulkImport: (rows: BulkImportRow[], replaceExisting = false) =>
    http<BulkImportResult>("/students/bulk-import", {
      method: "POST",
      body: { rows, dedupe_by: "gr_no", replace_existing: replaceExisting },
    }),
  importExcel: async (file: File, replaceExisting = false): Promise<BulkImportResult> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("lfs_erp_token");
    const url = new URL(
      `/api/students/import-excel?replace_existing=${replaceExisting}`,
      window.location.origin,
    );
    const res = await fetch(url.toString(), {
      method: "POST",
      body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      let detail: any = null;
      try { detail = await res.json(); } catch { /* ignore */ }
      throw new Error(detail?.detail || res.statusText || "Import failed");
    }
    return res.json();
  },
  uploadPhoto: async (id: string, file: File): Promise<Student> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("lfs_erp_token");
    const res = await fetch(`/api/students/${id}/photo`, {
      method: "POST",
      body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      let detail: any = null;
      try { detail = await res.json(); } catch { /* ignore */ }
      throw new Error(detail?.detail || res.statusText || "Photo upload failed");
    }
    return res.json();
  },
  removePhoto: (id: string) => http<Student>(`/students/${id}/photo`, { method: "DELETE" }),
};

// ---------------- EXAMS v2 ----------------
export interface RubricComponent {
  name: string;
  max_marks: number;
  weightage_pct?: number;
}
export interface ExamCycle {
  id: string; name: string; term?: string | null; type: string;
  starts_on: string; ends_on: string; result_status: string;
  created_by_name: string; created_at: string;
}
export interface ExamPaper {
  id: string; cycle_id: string; class_name: string; subject: string;
  scheduled_on: string; total_marks: number; passing_marks: number; result_status: string;
  rubrics?: RubricComponent[];
}
export interface PaperRosterEntry {
  student_id: string; student_name: string; roll_no: string;
  marks_obtained: number | null;
  component_marks: Record<string, number | undefined> | null;
  is_absent: boolean; is_exempted: boolean;
  grade: string | null; teacher_remarks: string | null;
}
export interface ReportCard {
  id: string; cycle_id: string; cycle_name: string; student_id: string; student_name: string;
  class_name: string; total_obtained: number; total_max: number; pct: number;
  grade_overall: string; gpa?: number | null; rank_in_section?: number | null;
  attendance_pct?: number | null;
  class_teacher_remark?: string | null; principal_remark?: string | null;
  conduct_remark?: string | null; discipline_remark?: string | null;
  released_to_parents?: boolean;
  released_at?: string | null;
  released_by?: string | null;
  payload: { subjects: any[]; totals: any; remark?: string };
}
export interface CycleCreatePayload {
  name: string; term?: string; type?: string;
  starts_on: string; ends_on: string;
  papers?: { class_name: string; subject: string; scheduled_on: string; total_marks?: number }[];
}
export const examsService = {
  cycles: () => http<ExamCycle[]>("/exams/cycles"),
  cycle: (id: string) => http<{ cycle: ExamCycle; papers: ExamPaper[] }>(`/exams/cycles/${id}`),
  createCycle: (data: CycleCreatePayload) =>
    http<ExamCycle>("/exams/cycles", { method: "POST", body: data }),
  deleteCycle: (id: string, force = false) =>
    http<{
      ok: true;
      deleted: { cycle: number; papers: number; marks: number; report_cards: number };
    }>(`/exams/cycles/${id}`, { method: "DELETE", query: force ? { force: true } : undefined }),
  publish: (id: string) => http<{ published: number; result_status: string }>(`/exams/cycles/${id}/publish`, { method: "POST" }),
  unpublish: (id: string) => http<any>(`/exams/cycles/${id}/unpublish`, { method: "POST" }),
  releaseAll: (id: string) =>
    http<{ released: number; cycle_id: string }>(`/exams/cycles/${id}/release-all`, { method: "POST" }),
  unreleaseAll: (id: string) =>
    http<{ unreleased: number; cycle_id: string }>(`/exams/cycles/${id}/unrelease-all`, { method: "POST" }),
  releaseCard: (rcId: string) =>
    http<ReportCard>(`/exams/report-cards/${rcId}/release`, { method: "POST" }),
  unreleaseCard: (rcId: string) =>
    http<ReportCard>(`/exams/report-cards/${rcId}/unrelease`, { method: "POST" }),
  reportCards: (params: { cycle_id?: string; class_name?: string; student_id?: string } = {}) =>
    http<ReportCard[]>("/exams/report-cards", { query: params }),
  myReportCards: () => http<ReportCard[]>("/exams/report-cards/me"),
  updateRemarks: (rcId: string, data: {
    class_teacher_remark?: string; principal_remark?: string;
    conduct_remark?: string; discipline_remark?: string; attendance_pct?: number;
  }) => http<ReportCard>(`/exams/report-cards/${rcId}/remarks`, { method: "PATCH", body: data }),
  // Rubrics + marks
  paperMarks: (paperId: string) =>
    http<{ paper: ExamPaper; roster: PaperRosterEntry[] }>(`/exams/papers/${paperId}/marks`),
  setRubrics: (paperId: string, rubrics: RubricComponent[]) =>
    http<ExamPaper>(`/exams/papers/${paperId}/rubrics`, { method: "PUT", body: { rubrics } }),
  saveMarks: (paperId: string, records: {
    student_id: string;
    marks_obtained?: number | null;
    component_marks?: Record<string, number>;
    is_absent?: boolean; is_exempted?: boolean;
    teacher_remarks?: string | null;
  }[]) => http<{ upserted: number; skipped?: { student_id: string; reason?: string }[] }>(`/exams/papers/${paperId}/marks/bulk`, {
    method: "POST", body: { records },
  }),
  // Excel export / import of marks
  fetchMarksExcel: async (paperId: string): Promise<Blob> => {
    const token = localStorage.getItem("lfs_erp_token");
    const res = await fetch(`/api/exams/papers/${paperId}/export-marks`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const t = await res.text();
      try { throw new Error(JSON.parse(t).detail || t); } catch { throw new Error(t); }
    }
    return res.blob();
  },
  importMarksExcel: async (
    paperId: string, file: File,
  ): Promise<{ upserted: number; total_rows: number; skipped: { row: number; gr_no: string; name: string; reason: string }[] }> => {
    const token = localStorage.getItem("lfs_erp_token");
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`/api/exams/papers/${paperId}/import-marks`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      const t = await res.text();
      try { throw new Error(JSON.parse(t).detail || t); } catch { throw new Error(t); }
    }
    return res.json();
  },
  // Cycle-level report card remarks export / import
  fetchCycleRemarksExcel: async (cycleId: string, className?: string): Promise<Blob> => {
    const token = localStorage.getItem("lfs_erp_token");
    const qs = className ? `?class_name=${encodeURIComponent(className)}` : "";
    const res = await fetch(`/api/exams/cycles/${cycleId}/export-remarks${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const t = await res.text();
      try { throw new Error(JSON.parse(t).detail || t); } catch { throw new Error(t); }
    }
    return res.blob();
  },
  importCycleRemarksExcel: async (
    cycleId: string, file: File, className?: string,
  ): Promise<{ upserted: number; total_rows: number; skipped: { row: number; gr_no: string; name: string; reason: string }[] }> => {
    const token = localStorage.getItem("lfs_erp_token");
    const form = new FormData();
    form.append("file", file);
    const qs = className ? `?class_name=${encodeURIComponent(className)}` : "";
    const res = await fetch(`/api/exams/cycles/${cycleId}/import-remarks${qs}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      const t = await res.text();
      try { throw new Error(JSON.parse(t).detail || t); } catch { throw new Error(t); }
    }
    return res.json();
  },
  // Absolute URL for PDF (opens in new tab with Bearer via inline auth token in href — use fetch to blob)
  reportCardPdfUrl: (rcId: string) => `/api/exams/report-cards/${rcId}/pdf`,
  fetchReportCardPdf: async (rcId: string): Promise<Blob> => {
    const token = localStorage.getItem("lfs_erp_token");
    const res = await fetch(`/api/exams/report-cards/${rcId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error(`PDF download failed (${res.status})`);
    return res.blob();
  },
  fetchBulkReportCardsPdf: async (
    cycleId: string,
    params: { class_name?: string; only_released?: boolean } = {},
  ): Promise<Blob> => {
    const token = localStorage.getItem("lfs_erp_token");
    const search = new URLSearchParams();
    if (params.class_name) search.set("class_name", params.class_name);
    if (params.only_released) search.set("only_released", "true");
    const qs = search.toString();
    const res = await fetch(
      `/api/exams/cycles/${cycleId}/report-cards/pdf-bulk${qs ? "?" + qs : ""}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
    );
    if (!res.ok) {
      const t = await res.text();
      try { throw new Error(JSON.parse(t).detail || t); } catch { throw new Error(t); }
    }
    return res.blob();
  },
};

export interface ExamSchedule {
  id: string; name: string; academic_year: string;
  start_date: string; end_date: string;
  applies_to: string[]; description?: string | null;
  created_at: string;
}
export interface ExamSchedulePayload {
  name: string; academic_year?: string;
  start_date: string; end_date: string;
  applies_to: string[]; description?: string | null;
}
export const examSchedulesService = {
  list: (params?: { academic_year?: string; class_name?: string }) =>
    http<ExamSchedule[]>("/exam-schedules", { query: params }),
  create: (data: ExamSchedulePayload) =>
    http<ExamSchedule>("/exam-schedules", { method: "POST", body: data }),
  update: (id: string, data: Partial<ExamSchedulePayload>) =>
    http<ExamSchedule>(`/exam-schedules/${id}`, { method: "PUT", body: data }),
  remove: (id: string) =>
    http<{ ok: true }>(`/exam-schedules/${id}`, { method: "DELETE" }),
  generatePreview: (id: string, params: { subjects?: string; total_marks?: number } = {}) =>
    http<{
      total_exams: number;
      schedule_name: string;
      plans: { class_name: string; subjects: string[]; total_marks: number; dates: string[] }[];
    }>(`/exam-schedules/${id}/generate/preview`, { query: params }),
  generate: (id: string, data: { subjects?: string[]; total_marks?: number; overwrite?: boolean }) =>
    http<{ created: number; updated: number; skipped: number; total_marks: number; schedule_name: string }>(
      `/exam-schedules/${id}/generate`, { method: "POST", body: data },
    ),
};

// ---------------- NOTIFICATIONS ----------------
export interface Notification {
  id: string; title: string; message: string;
  target_role?: string | null; target_class?: string | null;
  target_users?: string[]; read_by?: string[];
  created_by_name: string; created_at: string;
  unread?: boolean;
}
export const notificationsService = {
  list: (params?: { role_filter?: string; unread_only?: boolean }) =>
    http<Notification[]>("/notifications", { query: params }),
  create: (data: Partial<Notification>) => http<Notification>("/notifications", { method: "POST", body: data }),
  markRead: (id: string) => http<{ ok: true }>(`/notifications/${id}/read`, { method: "POST" }),
  markAllRead: () => http<{ marked: number }>("/notifications/read-all", { method: "POST" }),
  unreadCount: () => http<{ count: number }>("/notifications/unread-count"),
  classes: () => http<string[]>("/notifications/classes"),
  recipientsPreview: (target_role?: string | null, target_class?: string | null) =>
    http<{ count: number }>("/notifications/recipients-preview", {
      query: {
        ...(target_role ? { target_role } : {}),
        ...(target_class ? { target_class } : {}),
      },
    }),
};

// ---------------- ATTENDANCE ----------------
export interface AttendanceRow {
  id: string; student_id: string; student_name: string;
  class_name: string; date: string; status: "present" | "absent" | "late" | "excused" | "holiday";
  subject?: string | null;
  recorded_by_name?: string;
}
export interface AttendanceSummary {
  student_id: string;
  total_days: number;
  present: number;
  absent: number;
  late: number;
  excused: number;
  attendance_pct: number;
  history?: AttendanceRow[];
}
export const attendanceService = {
  list: (params: { class_name?: string; date?: string } = {}) =>
    http<AttendanceRow[]>("/attendance", { query: params }),
  bulkUpsert: (records: { student_id: string; date: string; status: string; class_name: string; subject?: string }[]) =>
    http<{ upserted: number }>("/attendance/bulk", { method: "POST", body: { records } }),
  summary: (studentId: string) =>
    http<AttendanceSummary>(`/attendance/students/${studentId}/summary`),
  myList: () => http<AttendanceRow[]>("/attendance"),
  // Bulk historical import (admin + examhead only)
  exportMonthlyTemplate: async (className: string, month: string): Promise<Blob> => {
    const token = localStorage.getItem("lfs_erp_token");
    const qs = `?class_name=${encodeURIComponent(className)}&month=${encodeURIComponent(month)}`;
    const res = await fetch(`/api/attendance/export-template${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const t = await res.text();
      try { throw new Error(JSON.parse(t).detail || t); } catch { throw new Error(t); }
    }
    return res.blob();
  },
  importMonthly: async (
    className: string, month: string, file: File,
  ): Promise<{ upserted: number; students_matched: number; total_rows: number; skipped: { row: number; gr_no: string; name: string; reason: string }[] }> => {
    const token = localStorage.getItem("lfs_erp_token");
    const form = new FormData();
    form.append("file", file);
    const qs = `?class_name=${encodeURIComponent(className)}&month=${encodeURIComponent(month)}`;
    const res = await fetch(`/api/attendance/import-monthly${qs}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      const t = await res.text();
      try { throw new Error(JSON.parse(t).detail || t); } catch { throw new Error(t); }
    }
    return res.json();
  },
};

// ---------------- TIMETABLE ----------------
export type Weekday = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat";
export interface TimetableEntry {
  id: string;
  class_name: string;
  day: Weekday;
  period: number;
  subject: string;
  teacher_name: string;
  start_time: string;
  end_time: string;
}
export interface TimetableImportSummary {
  classes_imported: number;
  entries_created: number;
  teachers_seen: number;
  staff_created: number;
  users_created: number;
  warnings: string[];
  filename?: string;
}
export const timetableService = {
  list: (class_name?: string) =>
    http<TimetableEntry[]>("/timetable", { query: class_name ? { class_name } : {} }),
  create: (data: Omit<TimetableEntry, "id">) =>
    http<TimetableEntry>("/timetable", { method: "POST", body: data }),
  remove: (id: string) => http<any>(`/timetable/${id}`, { method: "DELETE" }),
  importXlsx: async (file: File): Promise<TimetableImportSummary> => {
    const token = localStorage.getItem("lfs_erp_token");
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/timetable/import", {
      method: "POST",
      body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const err = await res.text();
      try { throw new Error(JSON.parse(err).detail || err); } catch { throw new Error(err); }
    }
    return res.json();
  },
};

// ---------------- STAFF ----------------
export interface Staff {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  role: string; // teacher / principal / accountant / non_teaching
  subject?: string | null;
  class_assigned?: string | null;
  employee_id?: string | null;
  designation?: string | null;
  qualification?: string | null;
  date_of_joining?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  address?: string | null;
  blood_group?: string | null;
  emergency_contact?: string | null;
  status?: "active" | "inactive";
}
export interface StaffPayload {
  name: string;
  email: string;
  role: string;
  phone?: string;
  subject?: string;
  class_assigned?: string;
  employee_id?: string;
  designation?: string;
  qualification?: string;
  date_of_joining?: string;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  blood_group?: string;
  emergency_contact?: string;
  status?: string;
}
export interface StaffImportSummary {
  rows_read: number;
  staff_matched_updated: number;
  staff_created: number;
  users_updated: number;
  users_created: number;
  timetable_rows_renamed: number;
  warnings: string[];
  filename?: string;
}
export const staffService = {
  list: () => http<Staff[]>("/staff"),
  create: (data: StaffPayload) => http<Staff>("/staff", { method: "POST", body: data }),
  update: (id: string, data: Partial<StaffPayload>) =>
    http<Staff>(`/staff/${id}`, { method: "PUT", body: data }),
  remove: (id: string) => http<{ ok: boolean }>(`/staff/${id}`, { method: "DELETE" }),
  importTeachers: async (file: File): Promise<StaffImportSummary> => {
    const token = localStorage.getItem("lfs_erp_token");
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/staff/import-teachers", {
      method: "POST",
      body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const err = await res.text();
      try { throw new Error(JSON.parse(err).detail || err); } catch { throw new Error(err); }
    }
    return res.json();
  },
};

// ---------------- CASHIER FEES ----------------
export type Quarter = 1 | 2 | 3 | 4;
export type PaymentMode = "cash" | "online" | "cheque";
export type Stream = "science" | "commerce" | "general";

export interface QuarterInfo {
  quarter: Quarter;
  label: string;
  months: string[];
  due_date: string;
  tuition_due: number;
  tuition_paid: number;
  tuition_pending: number;
  late_fee_due: number;
  late_fee_paid: number;
  late_fee_pending: number;
  late_fee_reason: string;
  days_past_due: number;
  due: number;
  paid: number;
  pending: number;
  status: "paid" | "partial" | "pending";
  payments: FeePayment[];
}
export interface FeePayment {
  id: string;
  receipt_no: string;
  student_id: string;
  gr_no?: string | null;
  student_name: string;
  class_name: string;
  section?: string | null;
  stream: Stream;
  academic_year: string;
  quarter: Quarter;
  months: string[];
  amount: number;
  tuition_amount?: number;
  late_fee_amount?: number;
  days_past_due?: number;
  payment_mode: PaymentMode;
  transaction_no?: string | null;
  cheque_no?: string | null;
  cheque_bank?: string | null;
  cheque_date?: string | null;
  note?: string | null;
  collected_by_email?: string | null;
  collected_by_name?: string | null;
  created_at: string;
}
export interface LateFeeRule {
  enabled: boolean;
  mode: "per_day" | "flat" | "off";
  grace_days: number;
  per_day_amount: number;
  flat_amount: number;
  max_amount: number;
}
export interface StudentFeeSummary {
  student: Student | null;
  stream: Stream;
  academic_year: string;
  late_fee_rule: LateFeeRule;
  quarters: QuarterInfo[];
  payments: FeePayment[];
}
export interface CollectFeePayload {
  student_id: string;
  quarter: Quarter;
  academic_year?: string;
  stream?: Stream;
  amount?: number | null;
  include_late_fee?: boolean;
  override_late_fee?: number | null;
  payment_mode: PaymentMode;
  transaction_no?: string;
  cheque_no?: string;
  cheque_bank?: string;
  cheque_date?: string;
  note?: string;
}
export interface CashbookRow {
  receipt_no: string; student_name: string; gr_no?: string | null; class_name: string;
  section?: string | null; quarter: Quarter; payment_mode: PaymentMode;
  amount: number; tuition_amount?: number; late_fee_amount?: number;
  transaction_no?: string | null; cheque_no?: string | null;
  created_at: string;
}
export interface CashbookSummary {
  count: number; total: number;
  by_mode: { cash: number; online: number; cheque: number };
  tuition_total: number; late_fee_total: number;
}
export const feeStructuresService = {
  list: () => http<{
    structures: FeeStructure[];
    classes: string[];
    rows: { class_name: string; section: string | null }[];
    configured: string[];
  }>("/fees/structures"),
  upsert: (className: string, section: string | null, data: { annual_amount: number; q1: number; q2: number; q3: number; q4: number }) =>
    http<FeeStructure>(`/fees/structures/${encodeURIComponent(className)}`, { method: "PUT", body: data, query: section ? { section } : undefined }),
  remove: (className: string, section: string | null) =>
    http<{ ok: true; deleted: string }>(`/fees/structures/${encodeURIComponent(className)}`, { method: "DELETE", query: section ? { section } : undefined }),
};

export interface FeeStructure {
  class_name: string;
  section?: string | null;
  annual_amount: number;
  q1: number; q2: number; q3: number; q4: number;
  updated_at?: string;
  updated_by_name?: string;
}

export const cashierService = {
  slab: (class_name: string, stream: Stream = "general") =>
    http<{ class_name: string; stream: Stream; quarters: { quarter: Quarter; label: string; months: string[]; amount: number }[] }>(
      "/fees/cashier/slab", { query: { class_name, stream } },
    ),
  studentSummary: (gr_no: string) =>
    http<StudentFeeSummary>(`/fees/cashier/student/${encodeURIComponent(gr_no)}`),
  collect: (data: CollectFeePayload) =>
    http<FeePayment>("/fees/cashier/collect", { method: "POST", body: data }),
  receiptPdfUrl: (receipt_no: string) =>
    `/api/fees/cashier/receipt/${encodeURIComponent(receipt_no)}/pdf`,
  openReceiptPdf: async (receipt_no: string) => {
    const token = localStorage.getItem("lfs_erp_token");
    const res = await fetch(`/api/fees/cashier/receipt/${encodeURIComponent(receipt_no)}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error("Failed to load PDF");
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
  getLateFeeRule: () => http<LateFeeRule>("/fees/cashier/late-fee-rule"),
  setLateFeeRule: (rule: LateFeeRule) =>
    http<LateFeeRule>("/fees/cashier/late-fee-rule", { method: "PUT", body: rule }),
  collections: (start_date: string, end_date?: string) =>
    http<{ rows: CashbookRow[]; summary: CashbookSummary }>(
      "/fees/cashier/reports/collections",
      { query: end_date ? { start_date, end_date } : { start_date } },
    ),
  cashbookDownload: async (date_str: string, format: "pdf" | "xlsx") => {
    const token = localStorage.getItem("lfs_erp_token");
    const res = await fetch(`/api/fees/cashier/reports/cashbook.${format}?date_str=${encodeURIComponent(date_str)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cashbook-${date_str}.${format}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  },
};


// ── AUTH / USER MANAGEMENT ────────────────────────────────────────
export interface UserSummary {
  id: string; email: string; name: string; role: string;
  class_name?: string | null; child_email?: string | null; phone?: string | null;
  is_active: boolean; must_reset_password: boolean;
  permissions?: string[]; permissions_custom?: boolean;
}
export const authAdminService = {
  changePassword: (old_password: string, new_password: string) =>
    http<{ ok: true }>("/auth/change-password", { method: "POST", body: { old_password, new_password } }),
  listUsers: (params?: { role?: string; q?: string; limit?: number }) =>
    http<UserSummary[]>("/auth/admin/users", { query: params }),
  resetPassword: (user_id: string, new_password: string, require_reset_on_login = true) =>
    http<{ ok: true; email: string }>(`/auth/admin/users/${user_id}/reset-password`, {
      method: "POST", body: { new_password, require_reset_on_login },
    }),
  updatePermissions: (user_id: string, permissions: string[] | null, reset = false) =>
    http<UserSummary>(`/auth/admin/users/${user_id}/permissions`, {
      method: "PUT", body: { permissions, reset },
    }),
  updateUser: (user_id: string, data: { email?: string; role?: string; name?: string }) =>
    http<UserSummary>(`/auth/admin/users/${user_id}`, { method: "PUT", body: data }),
  createUser: (data: { email: string; name: string; role: string; password: string }) =>
    http<UserSummary>(`/auth/admin/users`, { method: "POST", body: data }),
  deleteUser: (user_id: string) =>
    http<{ ok: true; deleted: string; email: string; staff_deactivated: number }>(
      `/auth/admin/users/${user_id}`, { method: "DELETE" }),
};
export const provisioningService = {
  parentLogins: (class_name?: string) =>
    http<{ created: number; existing: number; total: number }>(
      "/students/provision-parent-logins",
      { method: "POST", query: class_name ? { class_name } : {} },
    ),
  studentLogins: (class_names = "10,12") =>
    http<{ created: number; existing: number; total: number; classes: string[] }>(
      "/students/provision-student-logins",
      { method: "POST", query: { class_names } },
    ),
};

// ─────────────────── CLASS TESTS ───────────────────
export interface ClassTest {
  id: string; class_name: string; subject: string;
  topic: string; subtopic?: string | null;
  conducted_on: string; total_marks: number;
  remarks?: string | null;
  conducted_by?: string | null; conducted_by_name?: string | null;
  created_at?: string;
}
export interface ClassTestRosterEntry {
  student_id: string; student_name: string; roll_no?: string | null;
  marks_obtained: number | null; is_absent: boolean; remark?: string | null;
}
export interface StudentClassTestEntry {
  test: ClassTest;
  mark: {
    marks_obtained: number | null; is_absent: boolean;
    remark?: string | null; updated_at?: string;
  };
}
export const classTestsService = {
  list: (params: { class_name?: string; subject?: string } = {}) =>
    http<ClassTest[]>("/class-tests", { query: params }),
  create: (data: {
    class_name: string; subject: string; topic: string; subtopic?: string;
    conducted_on: string; total_marks?: number; remarks?: string;
  }) => http<ClassTest>("/class-tests", { method: "POST", body: data }),
  detail: (id: string) => http<{ test: ClassTest; roster: ClassTestRosterEntry[] }>(`/class-tests/${id}`),
  patch: (id: string, data: Partial<ClassTest>) =>
    http<ClassTest>(`/class-tests/${id}`, { method: "PATCH", body: data }),
  remove: (id: string) => http<{ ok: true }>(`/class-tests/${id}`, { method: "DELETE" }),
  saveMarks: (id: string, records: {
    student_id: string; marks_obtained?: number | null; is_absent?: boolean; remark?: string | null;
  }[]) => http<{ upserted: number }>(`/class-tests/${id}/marks`, {
    method: "POST", body: { records },
  }),
  mine: () => http<StudentClassTestEntry[]>("/class-tests/me"),
};

// ─────────────────── ACADEMICS (Lesson Plans, Logbook, Notebook Checks) ───────────────────
export interface LessonPlan {
  id: string; title: string;
  staff_id: string; staff_name: string;
  class_name: string; subject: string;
  term?: string | null;
  filename: string; file_ext: string; file_size: number;
  youtube_url?: string | null;
  uploaded_by?: string; uploaded_by_name?: string;
  uploaded_at: string; notes?: string | null;
}
export interface LogbookEntry {
  id: string; date: string; period: number;
  class_name: string; subject: string;
  topic: string; subtopic?: string | null;
  lesson_plan_id?: string | null;
  homework?: string | null; remarks?: string | null;
  teacher_id: string; teacher_name: string;
  submitted_at: string;
}
export interface NotebookCheck {
  id: string;
  student_id: string; student_name: string;
  class_name: string; subject: string;
  date: string; status: string; remark?: string | null;
  notebooks_checked: number;
  teacher_id: string; teacher_name: string;
  created_at: string;
}
export interface ComplianceRow {
  teacher_id: string; teacher_name: string;
  expected_days: number; submitted_days: number;
  compliance_pct: number; missing_dates: string[];
}
export const academicsService = {
  uploadPlan: async (data: {
    title: string; staff_id: string; class_name: string; subject: string;
    term?: string; notes?: string; youtube_url?: string; file?: File | null;
  }): Promise<LessonPlan> => {
    const token = localStorage.getItem("lfs_erp_token");
    const fd = new FormData();
    if (data.file) fd.append("file", data.file);
    fd.append("title", data.title);
    fd.append("staff_id", data.staff_id);
    fd.append("class_name", data.class_name);
    fd.append("subject", data.subject);
    if (data.term) fd.append("term", data.term);
    if (data.notes) fd.append("notes", data.notes);
    if (data.youtube_url) fd.append("youtube_url", data.youtube_url);
    const res = await fetch("/api/academics/lesson-plans/upload", {
      method: "POST", body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const err = await res.text();
      try { throw new Error(JSON.parse(err).detail || err); } catch { throw new Error(err); }
    }
    return res.json();
  },
  patchPlan: (id: string, data: { title?: string; notes?: string; youtube_url?: string | null }) =>
    http<LessonPlan>(`/academics/lesson-plans/${id}`, { method: "PATCH", body: data }),
  listPlans: (params: { staff_id?: string; class_name?: string; subject?: string } = {}) =>
    http<LessonPlan[]>("/academics/lesson-plans", { query: params }),
  removePlan: (id: string) =>
    http<{ ok: true }>(`/academics/lesson-plans/${id}`, { method: "DELETE" }),
  fetchPlanFile: async (id: string): Promise<Blob> => {
    const token = localStorage.getItem("lfs_erp_token");
    const res = await fetch(`/api/academics/lesson-plans/${id}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    return res.blob();
  },
  submitLogbook: (data: {
    date: string; period: number; class_name: string; subject: string;
    topic: string; subtopic?: string; lesson_plan_id?: string;
    homework?: string; remarks?: string;
  }) => http<LogbookEntry>("/academics/logbook", { method: "POST", body: data }),
  listLogbook: (params: {
    teacher_id?: string; class_name?: string; date_from?: string; date_to?: string;
  } = {}) => http<LogbookEntry[]>("/academics/logbook", { query: params }),
  compliance: (days = 14) =>
    http<{ days: number; since: string; teachers: ComplianceRow[] }>(
      "/academics/logbook/compliance", { query: { days } },
    ),
  addNotebookCheck: (data: {
    student_id: string; subject: string; date: string;
    status: string; remark?: string; notebooks_checked?: number;
  }) => http<NotebookCheck>("/academics/notebook-checks", { method: "POST", body: data }),
  listNotebookChecks: (params: {
    student_id?: string; class_name?: string; subject?: string;
    date_from?: string; date_to?: string;
  } = {}) => http<NotebookCheck[]>("/academics/notebook-checks", { query: params }),
  notebookReport: (params: { class_name?: string; subject?: string } = {}) =>
    http<{
      total_checks: number;
      students: {
        student_id: string; student_name: string; class_name: string;
        checks: number; by_status: Record<string, number>;
        last_checked: string | null; last_remark: string | null;
      }[];
    }>("/academics/notebook-checks/report", { query: params }),
};


